export type SupportedRegion = "IN" | "AE";
export type SiteRegion = SupportedRegion | "GLOBAL";

export type RegionSuggestion = {
  mode: "redirect" | "suggest";
  detectedCountry: SupportedRegion;
  targetRegion: SupportedRegion;
  targetLabel: string;
  targetUrl: string;
};

type RegionApiResponse = {
  country?: string | null;
};

const DISMISSED_REGION_STORAGE_KEY =
  "get2vacations-region-suggestion-dismissed";

const REGION_LABELS: Record<SupportedRegion, string> = {
  IN: "India",
  AE: "UAE",
};

const COUNTRY_TO_REGION: Partial<Record<string, SupportedRegion>> = {
  IN: "IN",
  AE: "AE",
};

const TARGET_URLS: Record<SupportedRegion, string> = {
  IN:
    import.meta.env.VITE_INDIA_SITE_URL?.toString().trim().replace(/\/$/, "") ||
    "https://get2vacation-in.vercel.app/",
  AE:
    import.meta.env.VITE_UAE_SITE_URL?.toString().trim().replace(/\/$/, "") ||
    "https://get2vacation-uae.vercel.app/",
};

const siteRegion = normalizeSiteRegion(import.meta.env.VITE_SITE_REGION);

function normalizeSiteRegion(value: unknown): SiteRegion {
  const normalized = value?.toString().trim().toUpperCase();

  if (normalized === "IN" || normalized === "AE") {
    return normalized;
  }

  return "GLOBAL";
}

async function fetchDetectedCountry(): Promise<SupportedRegion | null> {
  const response = await fetch("/api/region", {
    headers: {
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as RegionApiResponse;
  const country = payload.country?.toUpperCase();

  if (!country) {
    return null;
  }

  return COUNTRY_TO_REGION[country] ?? null;
}

function buildRedirectUrl(baseUrl: string) {
  if (typeof window === "undefined") {
    return baseUrl;
  }

  const destination = new URL(baseUrl);
  destination.pathname = window.location.pathname;
  destination.search = window.location.search;
  destination.hash = window.location.hash;

  return destination.toString();
}

function isDismissed(targetRegion: SupportedRegion) {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.sessionStorage.getItem(DISMISSED_REGION_STORAGE_KEY) === targetRegion
  );
}

export async function detectVisitorRegion(): Promise<SupportedRegion | null> {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return await fetchDetectedCountry();
  } catch {
    return null;
  }
}

export function getRegionalSiteUrl(region: SupportedRegion) {
  return buildRedirectUrl(TARGET_URLS[region]);
}

export function dismissRegionSuggestion(targetRegion: SupportedRegion) {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    DISMISSED_REGION_STORAGE_KEY,
    targetRegion,
  );
}

export async function resolveRegionSuggestion(): Promise<RegionSuggestion | null> {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const detectedCountry = await fetchDetectedCountry();

    if (!detectedCountry || detectedCountry === siteRegion) {
      return null;
    }

    if (siteRegion !== "GLOBAL" && isDismissed(detectedCountry)) {
      return null;
    }

    const targetBaseUrl = TARGET_URLS[detectedCountry];
    const currentOrigin = window.location.origin.replace(/\/$/, "");

    if (!targetBaseUrl || currentOrigin === targetBaseUrl) {
      return null;
    }

    return {
      mode: siteRegion === "GLOBAL" ? "redirect" : "suggest",
      detectedCountry,
      targetRegion: detectedCountry,
      targetLabel: REGION_LABELS[detectedCountry],
      targetUrl: buildRedirectUrl(targetBaseUrl),
    };
  } catch {
    return null;
  }
}
